import { validator } from "@/utils/validator";
import { MAX_TEXT_INPUT_VALIDATION } from "@/constants/common";
import {
  BILLING_CYCLE,
  PRICE_DIMENSION,
  PRODUCT_LIMITS,
  REFUND_TYPE,
  TENURE_OPERATOR,
} from "@/features/organization/product/constants/productEnums";
const DIGITS = /^-?\d+$/;
const DIGITS_MSG = "半角数字で入力してください。";
const priceMax = `${PRODUCT_LIMITS.MAX_PRICE.toLocaleString("en-US")}以下で入力してください。`;
const priceMin = `${(-PRODUCT_LIMITS.MAX_PRICE).toLocaleString("en-US")}以上で入力してください。`;
const monthsMax = `${PRODUCT_LIMITS.MAX_MONTHS}以下で入力してください。`;

/** Optional digit-string field capped at `max` (empty -> null). Caller appends required/notRequired. */
const numberField = (label: string, max: number, maxMsg: string) =>
  validator
    .string()
    .nullable()
    .emptyToNull()
    .matches(DIGITS, { message: DIGITS_MSG, excludeEmptyString: true })
    .test("max", `${label}は${maxMsg}`, (v?: string | null) => !v || Number(v) <= max)
    .label(label);

/**
 * 単価は負値を許可する（返金用の商品はマイナス金額で登録するため）。上限 MAX_PRICE / 下限 -MAX_PRICE で、
 * backend の @Min(-MAX_MONEY)/@Max(MAX_MONEY) と対称。値引き系フィールドは別途 nonNegative で 0 以上に制限する。
 */
const moneyField = (label: string) =>
  numberField(label, PRODUCT_LIMITS.MAX_PRICE, priceMax).test(
    "min",
    `${label}は${priceMin}`,
    (v?: string | null) => !v || Number(v) >= -PRODUCT_LIMITS.MAX_PRICE,
  );

const NON_NEGATIVE_MSG = "0以上で入力してください。";
const nonNegative = (value?: string | null) => value == null || value === "" || Number(value) >= 0;

/** `field` must be ≤ the sibling digit-string `otherKey` (both optional). */
const lteSibling = (value: string | null | undefined, ctx: { parent: Record<string, unknown> }, otherKey: string) => {
  const other = ctx.parent[otherKey] as string | null | undefined;
  if (!value || !other) return true;
  return Number(value) <= Number(other);
};

const typePriceItem = validator.object().shape({
  nationalityId: validator.string().nullable(),
  fieldId: validator.string().nullable(),
  // ティア / その他 の項目は名前が必須（空欄不可）。組み合わせ条件はこの行を id で参照するため、
  // 単価や上限/下限を入力した意味のある行には必ず名前を求める（既定行 / 空行は対象外）。
  variantValue: validator
    .string()
    .trim()
    .emptyToNull()
    .nullable()
    .max(MAX_TEXT_INPUT_VALIDATION)
    .test("tier-name-required", "", (value, ctx) => {
      const parent = ctx.parent as {
        dimension?: string;
        isDefault?: boolean;
        unitPrice?: string | null;
        lowerLimit?: string | null;
        upperLimit?: string | null;
      };
      const isTierOrOther =
        parent.dimension === PRICE_DIMENSION.TIER || parent.dimension === PRICE_DIMENSION.OTHER;
      if (!isTierOrOther || parent.isDefault) return true;
      const hasContent = !!parent.unitPrice || !!parent.lowerLimit || !!parent.upperLimit;
      if (!hasContent || value) return true;
      const label = parent.dimension === PRICE_DIMENSION.OTHER ? "項目名" : "ティア名";
      return ctx.createError({ message: `${label}を入力してください。` });
    }),
  unitPrice: moneyField("単価").notRequired(),
  // ティア 下限 / 上限 (人数). Kept in the schema so they survive submit; 上限 ≥ 下限.
  lowerLimit: numberField("下限", PRODUCT_LIMITS.MAX_PRICE, priceMax).notRequired(),
  upperLimit: numberField("上限", PRODUCT_LIMITS.MAX_PRICE, priceMax)
    .test("gte-lower", "上限は下限以上で入力してください。", (upperLimit, ctx) => {
      const lower = ctx.parent.lowerLimit as string | null | undefined;
      return !upperLimit || !lower || Number(upperLimit) >= Number(lower);
    })
    .notRequired(),
});

const combinationItem = validator.object().shape({
  nationalityId: validator.string().nullable(),
  fieldId: validator.string().nullable(),
  variantValue: validator.string().nullable(),
  unitPrice: moneyField("単価").notRequired(),
  discountTokutei2: moneyField("特定技能2号値引き")
    .test("gte-zero", `特定技能2号値引きは${NON_NEGATIVE_MSG}`, nonNegative)
    .test("lte-unit", "特定技能2号値引きは単価以下で入力してください。", (discount, ctx) => lteSibling(discount, ctx, "unitPrice"))
    .notRequired(),
  memo: validator.string().trim().emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION),
});

const refundItem = validator.object().shape({
  tenureMonths: numberField("在職期間", PRODUCT_LIMITS.MAX_MONTHS, monthsMax)
    .test("required-with-refund", "在職期間を入力してください。", (months, ctx) => !ctx.parent.refundValue || !!months)
    .notRequired(),
  tenureOperator: validator.string().oneOf(Object.values(TENURE_OPERATOR)),
  refundValue: numberField("返金額", PRODUCT_LIMITS.MAX_PRICE, priceMax)
    .test("required-with-tenure", "返金額を入力してください。", (refundValue, ctx) => !ctx.parent.tenureMonths || !!refundValue)
    .notRequired(),
  refundType: validator.string().oneOf(Object.values(REFUND_TYPE)),
});

export const productSchema = validator.object().shape({
  // 商品基本情報
  name: validator.string().trim().emptyToNull().label("商品名").max(PRODUCT_LIMITS.MAX_NAME).required(),
  billingCycle: validator.string().oneOf(Object.values(BILLING_CYCLE)).label("請求サイクル").required(),
  taxRateId: validator.string().nullable().label("税区分").required(),
  unit: validator.string().trim().emptyToNull().nullable().label("単位").max(PRODUCT_LIMITS.MAX_UNIT).notRequired(),
  unitPrice: moneyField("単価（税抜）").required(),
  discountTokutei2: moneyField("値引き（特定2号）")
    .test("gte-zero", `値引き（特定2号）は${NON_NEGATIVE_MSG}`, nonNegative)
    .test("lte-unit", "値引き（特定2号）は単価以下で入力してください。", (v, ctx) => lteSibling(v, ctx, "unitPrice"))
    .notRequired(),
  accountCategory: validator.string().trim().emptyToNull().nullable().label("科目区分").max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
  invoiceDisplayFormat: validator.string().nullable().notRequired(),
  isActive: validator.boolean().label("商品設定").notRequired(),
  description: validator.string().trim().emptyToNull().nullable().label("メモ").max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
  firstDiscountPeriod: numberField("初回割引期間", PRODUCT_LIMITS.MAX_MONTHS, monthsMax).notRequired(),
  firstDiscountPrice: moneyField("初回割引単価")
    .test("lte-unit", "初回割引単価は単価以下で入力してください。", (v, ctx) => lteSibling(v, ctx, "unitPrice"))
    .notRequired(),
  // 種別単価設定 / 単価組み合わせ条件設定
  nationalityEnabled: validator.boolean(),
  fieldEnabled: validator.boolean(),
  variantEnabled: validator.boolean(),
  typePrices: validator.array().of(typePriceItem),
  combinations: validator.array().of(combinationItem),
  // 返金条件設定
  refundConditions: validator.array().of(refundItem),
});
